import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCsrfCookie } from "../lib/utils";

export const RABBIT_HOLE_QUERY_KEY = "rabbit-hole-current";

export interface RabbitHoleWeek {
  id?: number;
  topic: string;
  language: string;
  week_start: string;
  week_end: string;
  status: "none" | "PLANNING" | "ACTIVE" | "COMPLETED";
  next_week_topic: string;
  created_at?: string;
  completed_discoveries?: number;
  read_discoveries?: number;
  total_discoveries?: number;
  discoveries?: RabbitHoleDiscovery[];
  journey_plan?: RabbitHoleJourneyPlan;
}

export interface RabbitHoleJourneyPlan {
  weekly_topic: string;
  entry_question: string;
  why_this_is_interesting: string;
  chapters: {
    day_index: number;
    phase: string;
    title: string;
    knowledge_goal: string;
  }[];
}

export interface RabbitHoleDiscovery {
  id: number;
  topic: string;
  language?: string;
  level?: string;
  word_number?: number;
  status: string;
  chatgpt_output?: string;
  created_at: string;
  origin?: string;
  rabbit_hole_week?: number;
  rabbit_hole_day_index: number;
  view_count: number;
  learning_progress: number;
  has_content?: boolean;
}

export interface RabbitHoleTopicSuggestion {
  id: number;
  topic: string;
  summary?: string;
}

/** Returned when a create request conflicts with an edition already in progress. */
export class RabbitHoleCreateError extends Error {
  constructor(
    message: string,
    public readonly code: string | null = null,
    public readonly weekId: number | null = null,
  ) {
    super(message);
  }
}

export interface RabbitHoleReport {
  topic: string;
  language: string;
  week_start: string;
  week_end: string;
  completed_discoveries: number;
  read_discoveries: number;
  total_discoveries: number;
  vocabulary_count: number;
  review_due_count: number;
}

export interface RabbitHoleKnowledgeMap {
  topic: string;
  language: string;
  concepts: { id: number; title: string; day_index: number }[];
  display_language: string;
}

export function syncRabbitHoleTopicSuggestionBatchCache(
  queryClient: ReturnType<typeof useQueryClient>,
  language: string,
  suggestion: RabbitHoleTopicSuggestion,
) {
  const nextLanguage = (language || "").trim().toUpperCase();
  if (!nextLanguage) return;

  queryClient.setQueriesData<Record<string, RabbitHoleTopicSuggestion[]>>(
    { queryKey: [RABBIT_HOLE_QUERY_KEY, "topic-suggestions-batch"] },
    (previous) => ({
      ...(previous ?? {}),
      [nextLanguage]: [suggestion],
    }),
  );
}

export function useRabbitHoleCurrent(language: string) {
  return useQuery<RabbitHoleWeek>({
    queryKey: [RABBIT_HOLE_QUERY_KEY, language],
    queryFn: async () => {
      const res = await fetch(
        `/api/reeprise/rabbit-hole-weeks/current/?language=${encodeURIComponent(language)}`,
      );
      if (!res.ok) {
        throw new Error("could not fetch rabbit hole week");
      }
      return res.json();
    },
    staleTime: 0,
    refetchInterval: (query) => {
      if (
        query.state.data?.discoveries?.some(
          (d) =>
            d.status === "PENDING" ||
            (d.topic || "").toLowerCase().startsWith("rabbit hole"),
        )
      ) {
        return 3000;
      }
      return false;
    },
  });
}

export function useRabbitHoleCreateWeek() {
  const queryClient = useQueryClient();

  return useMutation<
    RabbitHoleWeek,
    RabbitHoleCreateError,
    {
      topic: string;
      language: string;
      topic_source?: "MANUAL" | "IDEA";
      topic_suggestion_id?: number;
    }
  >({
    mutationFn: async ({ topic, language, topic_source, topic_suggestion_id }) => {
      const res = await fetch("/api/reeprise/rabbit-hole-weeks/create/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCsrfCookie(),
        },
        credentials: "same-origin",
        body: JSON.stringify({
          topic,
          language,
          ...(topic_source ? { topic_source } : {}),
          ...(typeof topic_suggestion_id === "number"
            ? { topic_suggestion_id }
            : {}),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new RabbitHoleCreateError(
          body.detail || "could not create rabbit hole week",
          body.code || null,
          typeof body.week_id === "number" ? body.week_id : null,
        );
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RABBIT_HOLE_QUERY_KEY] });
      queryClient.removeQueries({
        queryKey: [RABBIT_HOLE_QUERY_KEY, "topic-suggestions"],
      });
    },
  });
}

export function useRabbitHoleGenerateDiscovery() {
  const queryClient = useQueryClient();

  return useMutation<RabbitHoleDiscovery, Error, { weekId: number }>({
    mutationFn: async ({ weekId }) => {
      const res = await fetch(
        `/api/reeprise/rabbit-hole-weeks/${weekId}/generate-discovery/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCsrfCookie(),
          },
          credentials: "same-origin",
        },
      );
      if (!res.ok) {
        throw new Error("could not generate discovery");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RABBIT_HOLE_QUERY_KEY] });
    },
  });
}

export function useRabbitHoleSetNextTopic() {
  const queryClient = useQueryClient();

  return useMutation<{ next_week_topic: string }, Error, { weekId: number; topic: string }>({
    mutationFn: async ({ weekId, topic }) => {
      const res = await fetch(
        `/api/reeprise/rabbit-hole-weeks/${weekId}/set-next-topic/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCsrfCookie(),
          },
          credentials: "same-origin",
          body: JSON.stringify({ topic }),
        },
      );
      if (!res.ok) {
        throw new Error("could not set next topic");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RABBIT_HOLE_QUERY_KEY] });
    },
  });
}

export function useRabbitHoleReport(weekId: number | null) {
  return useQuery<RabbitHoleReport>({
    queryKey: [RABBIT_HOLE_QUERY_KEY, "report", weekId],
    queryFn: async () => {
      const res = await fetch(
        `/api/reeprise/rabbit-hole-weeks/${weekId}/report/`,
      );
      if (!res.ok) {
        throw new Error("could not fetch report");
      }
      return res.json();
    },
    enabled: typeof weekId === "number",
    staleTime: 30_000,
  });
}

export function useRabbitHoleKnowledgeMap(weekId: number | null, language?: string) {
  return useQuery<RabbitHoleKnowledgeMap>({
    queryKey: [RABBIT_HOLE_QUERY_KEY, "knowledge-map", weekId, language],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (language) {
        params.set("language", language);
      }
      const url = `/api/reeprise/rabbit-hole-weeks/${weekId}/knowledge-map/${params.toString() ? "?" + params.toString() : ""}`;
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error("could not fetch knowledge map");
      }
      return res.json();
    },
    enabled: typeof weekId === "number",
    staleTime: 60_000,
  });
}

export function useRabbitHoleSuggestions() {
  return useMutation<string[], Error, { topic: string; language: string }>({
    mutationFn: async ({ topic, language }) => {
      const res = await fetch("/api/reeprise/rabbit-hole-weeks/suggestions/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCsrfCookie(),
        },
        credentials: "same-origin",
        body: JSON.stringify({ topic, language }),
      });
      if (!res.ok) {
        throw new Error("could not fetch suggestions");
      }
      const data = await res.json();
      return data.suggestions ?? [];
    },
  });
}

export function useRabbitHoleTopicSuggestions(language: string) {
  return useQuery<RabbitHoleTopicSuggestion[]>({
    queryKey: [RABBIT_HOLE_QUERY_KEY, "topic-suggestions", language],
    queryFn: async () => {
      const res = await fetch(
        `/api/reeprise/rabbit-hole-weeks/topic-suggestions/?language=${encodeURIComponent(language)}`,
        { credentials: "same-origin" },
      );
      if (!res.ok) {
        throw new Error("could not fetch topic suggestions");
      }
      const data = await res.json();
      return data.suggestions ?? [];
    },
    staleTime: 300_000,
    enabled: Boolean(language),
  });
}

export function useRabbitHoleTopicSuggestionsBatch(languages: string[]) {
  return useQuery<Record<string, RabbitHoleTopicSuggestion[]>>({
    queryKey: [RABBIT_HOLE_QUERY_KEY, "topic-suggestions-batch", languages.join(",")],
    queryFn: async () => {
      const res = await fetch(
        `/api/reeprise/rabbit-hole-weeks/topic-suggestions/?languages=${encodeURIComponent(
          languages.join(","),
        )}`,
        { credentials: "same-origin" },
      );
      if (!res.ok) {
        throw new Error("could not fetch rabbit hole topic suggestions");
      }
      const data = await res.json();
      return data.suggestions_by_language ?? {};
    },
    staleTime: 300_000,
    enabled: languages.length > 0,
  });
}

export function useRabbitHoleRefreshTopicSuggestion() {
  const queryClient = useQueryClient();

  return useMutation<RabbitHoleTopicSuggestion, Error, { language: string }>({
    mutationFn: async ({ language }) => {
      const res = await fetch(
        "/api/reeprise/rabbit-hole-weeks/topic-suggestions/refresh/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCsrfCookie(),
          },
          credentials: "same-origin",
          body: JSON.stringify({ language }),
        },
      );
      if (!res.ok) throw new Error("could not refresh rabbit hole topic");
      return res.json();
    },
    onSuccess: (suggestion, variables) => {
      syncRabbitHoleTopicSuggestionBatchCache(
        queryClient,
        variables.language,
        suggestion,
      );
      queryClient.setQueryData<RabbitHoleTopicSuggestion[]>(
        [
          RABBIT_HOLE_QUERY_KEY,
          "topic-suggestions",
          (variables.language || "").trim().toUpperCase(),
        ],
        [suggestion],
      );
      // The replacement response is the authoritative next suggestion. A broad
      // invalidation immediately refetches the active planning query and can
      // overwrite it with an in-flight response containing the dismissed topic.
      // The two visible suggestion caches above are updated directly instead.
    },
  });
}

export function useRabbitHoleAbandon() {
  const queryClient = useQueryClient();

  return useMutation<{ detail: string }, Error, { weekId: number }>({
    mutationFn: async ({ weekId }) => {
      const res = await fetch(
        `/api/reeprise/rabbit-hole-weeks/${weekId}/abandon/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCsrfCookie(),
          },
          credentials: "same-origin",
        },
      );
      if (!res.ok) {
        throw new Error("could not abandon week");
      }
      return res.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [RABBIT_HOLE_QUERY_KEY] });
    },
  });
}

export function useRabbitHoleRegenerateTitle() {
  const queryClient = useQueryClient();

  return useMutation<
    { id: number; topic: string },
    Error,
    { weekId: number; discoveryId: number }
  >({
    mutationFn: async ({ weekId, discoveryId }) => {
      const res = await fetch(
        `/api/reeprise/rabbit-hole-weeks/${weekId}/discoveries/${discoveryId}/regenerate-title/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCsrfCookie(),
          },
          credentials: "same-origin",
        },
      );
      if (!res.ok) {
        throw new Error("could not regenerate title");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RABBIT_HOLE_QUERY_KEY] });
    },
  });
}

interface RabbitHoleWeekSummary {
  language: string;
  topic: string;
  completed: number;
  total: number;
  week_id: number;
}

export function useRabbitHoleOverviewSummary(languages: string[]) {
  return useQuery<{ weeks: RabbitHoleWeekSummary[] }>({
    queryKey: [RABBIT_HOLE_QUERY_KEY, "summary", languages.join(",")],
    queryFn: async () => {
      const res = await fetch(
        `/api/reeprise/rabbit-hole-weeks/summary/?languages=${encodeURIComponent(languages.join(","))}`,
      );
      if (!res.ok) {
        throw new Error("could not fetch rabbit hole summary");
      }
      return res.json();
    },
    staleTime: 10_000,
    enabled: languages.length > 0,
  });
}

export function useRabbitHoleWeeksList(
  language?: string,
  opts?: { enabled?: boolean },
) {
  return useQuery<RabbitHoleWeek[]>({
    queryKey: [RABBIT_HOLE_QUERY_KEY, "weeks-list", language],
    queryFn: async () => {
      const url = new URL(
        "/api/reeprise/rabbit-hole-weeks/",
        window.location.origin,
      );
      if (language) url.searchParams.set("language", language);
      url.searchParams.set("limit", "50");
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error("could not fetch rabbit hole weeks");
      return res.json();
    },
    staleTime: 30_000,
    enabled: opts?.enabled ?? true,
  });
}

export function useRabbitHoleWeekDetail(weekId: number | null) {
  return useQuery<RabbitHoleWeek>({
    queryKey: [RABBIT_HOLE_QUERY_KEY, "week-detail", weekId],
    queryFn: async () => {
      const res = await fetch(
        `/api/reeprise/rabbit-hole-weeks/${weekId}/`,
      );
      if (!res.ok) throw new Error("could not fetch rabbit hole week");
      return res.json();
    },
    enabled: weekId !== null && typeof weekId === "number",
    staleTime: 0,
  });
}
